import { Trans, useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import type { CoachCause, CoachReport, CoachZone, CoachSpread } from "../lib/coach";
import { formatNumber } from "../lib/format";
import type { AlignedLap } from "../lib/trace";

interface Props {
  report: CoachReport;
  /** Every lap on screen, so the driver being coached can be swapped. */
  laps: AlignedLap[];
  onPickStudent: (key: string) => void;
  /** Emphasises the matching band on the charts. */
  onHoverZone: (key: string | null) => void;
  onFocusZone: (fromM: number, toM: number) => void;
}

/**
 * The two or three corners worth working on, and what was different about them.
 *
 * Deliberately short. The charts already show every difference between two laps;
 * what a driver cannot get from them is which differences paid for the lap time,
 * and that is the only thing this says. Each card is a stretch of track, the
 * seconds it cost, and at most three measurements — hovering one lights up the
 * matching band on the charts, clicking it zooms the stack to it.
 *
 * The coach hands over findings, not prose; the sentences are written here, out of
 * the catalogue for whichever language is being read.
 */
export function CoachPanel({
  report,
  laps,
  onPickStudent,
  onHoverZone,
  onFocusZone,
}: Props) {
  const { t } = useTranslation();
  const others = laps.filter((lap) => lap.key !== report.reference.key);
  const gap = report.lostSeconds;

  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-amber-500/25 bg-amber-500/[0.03]">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-amber-500/20 bg-amber-500/[0.06] px-3 py-2">
        <span className="text-xs font-medium uppercase tracking-wider text-amber-300/90">
          {t("coach.title")}
        </span>

        {others.length > 1 ? (
          <select
            value={report.student.key}
            onChange={(event) => onPickStudent(event.target.value)}
            className="rounded-md border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-xs text-zinc-200"
          >
            {others.map((lap) => (
              <option key={lap.key} value={lap.key}>
                {lap.label}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-sm text-zinc-200">{report.student.label}</span>
        )}

        <span className="text-xs text-zinc-500">{t("coach.against")}</span>
        <span className="text-sm text-zinc-300">{report.reference.label}</span>

        {gap > 0.01 && (
          <span className="ml-auto text-sm tabular-nums text-zinc-400">
            {/* Seconds read as prose here, not as a timing column, so they follow
                the reader's language like the sentences around them. */}
            <span className="text-red-400">+{formatNumber(gap, 2)} s</span>{" "}
            {t("coach.overLap")}
            {report.zones.length > 0 && (
              <span className="ml-2 text-zinc-500">
                ·{" "}
                {t("coach.share", {
                  count: report.zones.length,
                  percent: Math.round(report.covered * 100),
                })}
              </span>
            )}
          </span>
        )}
      </div>

      {report.zones.length === 0 ? (
        <p className="px-3 py-4 text-sm text-zinc-400">
          {gap > 0.01
            ? t("coach.spreadThin")
            : t("coach.level", { reference: report.reference.label })}
        </p>
      ) : (
        <div className="grid gap-px bg-amber-500/10 sm:grid-cols-2 xl:grid-cols-3">
          {report.zones.map((zone, index) => (
            <button
              key={zone.key}
              type="button"
              onMouseEnter={() => onHoverZone(zone.key)}
              onMouseLeave={() => onHoverZone(null)}
              onClick={() => onFocusZone(zone.fromM, zone.toM)}
              className="group bg-zinc-950/40 px-3 py-3 text-left transition-colors hover:bg-zinc-900/60"
            >
              <div className="flex items-baseline gap-2">
                <span className="flex size-4 shrink-0 items-center justify-center rounded bg-amber-500/20 text-[10px] font-semibold tabular-nums text-amber-300">
                  {index + 1}
                </span>
                <span className="text-sm font-medium text-zinc-100">
                  {zoneLabel(t, zone)}
                </span>
                {zone.corner && (
                  <span className="text-xs text-zinc-500">
                    {t(`coach.directions.${zone.corner.direction}`)}
                  </span>
                )}
                <span className="ml-auto text-sm tabular-nums text-red-400">
                  +{formatNumber(zone.lostSeconds, 2)} s
                </span>
              </div>

              {zone.emphasis === "both" && (
                <p className="mt-1 text-[11px] tabular-nums text-zinc-500">
                  {t("coach.split", {
                    corner: formatNumber(Math.max(0, zone.corneringSeconds), 2),
                    run: formatNumber(Math.max(0, zone.runSeconds), 2),
                  })}
                </p>
              )}

              <ul className="mt-2 space-y-1.5">
                {zone.causes.length === 0 ? (
                  <li className="text-xs leading-relaxed text-zinc-500">
                    {t(zone.emphasis === "run" ? "coach.noCauseRun" : "coach.noCause")}
                  </li>
                ) : (
                  zone.causes.map((cause) => (
                    <li
                      key={cause.kind}
                      className="text-xs leading-relaxed text-zinc-400"
                    >
                      {causeText(t, cause)}
                    </li>
                  ))
                )}
              </ul>

              <span className="mt-2 block text-[10px] uppercase tracking-wider text-zinc-600 group-hover:text-amber-300/70">
                {t("coach.zoom", {
                  from: Math.round(zone.fromM),
                  to: Math.round(zone.toM),
                })}
              </span>
            </button>
          ))}
        </div>
      )}

      {report.spreads.length > 0 && (
        <div className="border-t border-amber-500/20 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">
            {t("coach.leastRepeatable")}
          </p>
          <ul className="mt-1 space-y-1">
            {report.spreads.map((spread) => (
              <li key={spread.key} className="text-xs text-zinc-400">
                <Trans
                  i18nKey="coach.variesBy"
                  values={{
                    label: zoneLabel(t, spread),
                    seconds: formatNumber(spread.spreadSeconds, 2),
                    count: spread.lapCount,
                  }}
                  components={[
                    <span className="text-zinc-200" />,
                    <span className="tabular-nums text-amber-300" />,
                  ]}
                />
                {spread.note && (
                  <span className="text-zinc-500">
                    {" — "}
                    {t(`coach.notes.${spread.note.kind}`, spread.note.values)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * A zone runs from one braking point to the next, so naming it after its corner
 * alone would be a lie whenever the time went on the straight that follows. The
 * label says how far it reaches whenever that is where the loss is.
 */
function zoneLabel(t: TFunction, zone: CoachZone | CoachSpread): string {
  if (!zone.corner) {
    return zone.nextCorner
      ? t("coach.zones.lead", { next: zone.nextCorner })
      : t("workspace.range", {
          from: Math.round(zone.fromM),
          to: Math.round(zone.toM),
        });
  }

  const number = zone.corner.number;
  if (!("emphasis" in zone) || zone.emphasis === "corner") {
    return t("workspace.turn", { number });
  }

  return zone.nextCorner
    ? t("coach.zones.run", { number, next: zone.nextCorner })
    : t("coach.zones.runToLine", { number });
}

/**
 * Two findings quote something that is itself a word rather than a figure — a gear
 * and a part of the corner — so those are looked up before the sentence they go in.
 */
function causeText(t: TFunction, cause: CoachCause): string {
  if (cause.kind === "gear") {
    return t("coach.causes.gear", {
      mine: gearName(t, Number(cause.values.mineGear)),
      theirs: gearName(t, Number(cause.values.theirsGear)),
    });
  }

  if (cause.kind === "line_wide" || cause.kind === "line_tight") {
    return t(`coach.causes.${cause.kind}`, {
      ...cause.values,
      phase: t(`coach.phases.${cause.values.phase}`),
    });
  }

  return t(`coach.causes.${cause.kind}`, cause.values);
}

function gearName(t: TFunction, gear: number): string {
  if (gear <= 0) return t("coach.gears.neutral");
  return t("coach.gears.ordinal", { count: gear, ordinal: true });
}
